const WIDTH = 640;
const HEIGHT = 424;
const PADDING = 28;
const TILE_RADIUS = 28;
const FPS = 30;
const IDLE_FPS = 2;

interface Palette {
  gradientStart: string;
  gradientMiddle: string;
  gradientEnd: string;
  tile: string;
}

export class ScreenSharePipCompositor {
  readonly video: HTMLVideoElement;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly palette: Palette;
  private stopTicker: (() => void) | null = null;
  private active = false;

  constructor(private readonly source: HTMLVideoElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = WIDTH;
    this.canvas.height = HEIGHT;
    this.ctx = this.canvas.getContext('2d')!;
    this.palette = readPalette();

    this.video = document.createElement('video');
    this.video.muted = true;
    this.video.playsInline = true;
    Object.assign(this.video.style, {
      position: 'fixed',
      width: '1px',
      height: '1px',
      opacity: '0',
      pointerEvents: 'none',
      insetBlockEnd: '0',
      insetInlineEnd: '0',
    });
  }

  get isReady(): boolean {
    return this.video.readyState >= HTMLMediaElement.HAVE_METADATA;
  }

  start(): void {
    if (this.video.srcObject) return;
    this.draw();
    this.video.srcObject = this.canvas.captureStream(FPS);
    document.body.appendChild(this.video);
    this.video.play().catch(() => undefined);
    this.restartTicker();
  }

  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    if (this.video.srcObject) this.restartTicker();
  }

  stop(): void {
    this.stopTicker?.();
    this.stopTicker = null;
    this.active = false;
    (this.video.srcObject as MediaStream | null)?.getTracks().forEach((track) => track.stop());
    this.video.srcObject = null;
    this.video.remove();
  }

  private restartTicker(): void {
    this.stopTicker?.();
    this.stopTicker = startTicker(() => this.draw(), 1000 / (this.active ? FPS : IDLE_FPS));
  }

  private draw(): void {
    const { ctx, palette } = this;

    const gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
    gradient.addColorStop(0, palette.gradientStart);
    gradient.addColorStop(0.55, palette.gradientMiddle);
    gradient.addColorStop(1, palette.gradientEnd);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    const x = PADDING;
    const y = PADDING;
    const w = WIDTH - PADDING * 2;
    const h = HEIGHT - PADDING * 2;
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, TILE_RADIUS);
    ctx.clip();
    ctx.fillStyle = palette.tile;
    ctx.fillRect(x, y, w, h);
    const { videoWidth, videoHeight } = this.source;
    if (videoWidth && videoHeight) {
      const scale = Math.max(w / videoWidth, h / videoHeight);
      const dw = videoWidth * scale;
      const dh = videoHeight * scale;
      ctx.drawImage(this.source, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    }
    ctx.restore();
  }
}

function readPalette(): Palette {
  const scope = document.createElement('div');
  scope.className = 'theme-dark';
  scope.hidden = true;
  const probe = document.createElement('span');
  scope.appendChild(probe);
  document.body.appendChild(scope);

  const color = (value: string) => {
    probe.style.color = value;
    return getComputedStyle(probe).color;
  };
  const palette: Palette = {
    gradientStart: color('var(--bg-primary)'),
    gradientMiddle: color('color-mix(in srgb, var(--bg-brand-solid) 55%, var(--bg-primary))'),
    gradientEnd: color('color-mix(in srgb, var(--bg-success-solid) 55%, var(--bg-brand-solid))'),
    tile: color('var(--bg-secondary)'),
  };
  scope.remove();
  return palette;
}

function startTicker(tick: () => void, intervalMs: number): () => void {
  try {
    const source = `setInterval(() => postMessage(0), ${intervalMs});`;
    const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
    const worker = new Worker(url);
    worker.onmessage = tick;
    return () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  } catch {
    const handle = setInterval(tick, intervalMs);
    return () => clearInterval(handle);
  }
}
